import React from "react";
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from "react-redux";
import { PriceLabel } from '@ff-os/shared-react/components/price-label/PriceLabel';
import { useExternalFromPriceRender } from '@ff-os/shared-react/hooks/useExternalFromPriceRender';
import { useMobile } from "@ff-os/shared-react/hooks/useMobile";
import { urls } from "@ff-os/shared-utils/urls";
import { selectAllVariants } from '@ff-scene-plugins/photo-prints-redux/selectors/variantSelectors';
import { createUiBindings } from "@ff-scene-plugins/photo-prints-redux/slices/generalUiSlice";
import { PhotoPrintsJson } from '@ff-scene-plugins/photo-prints-shared/PhotoPrintsJson';
import classNames from "classnames";
import {
    setAccessoryId,
    setFrameId,
    selectAccessoryId,
    setIsFotofunBannerHidden,
    setFotoFunTab,
    setAccessoryIsDragging,
    setDraggedAccessory
} from "../../../_redux-ui";
import { DragCardHover } from "../components";
import styles from "./AccessoriesOptionTab.module.scss";

type Accessory = PhotoPrintsJson.Size;

type AccessoryItemProps = {
    accessory: Accessory;
    className?: string;
    onDragStart?: (event: React.DragEvent<HTMLDivElement>) => void;
    onDragEnd?: (event: React.DragEvent<HTMLDivElement>) => void;
};

export const AccessoryItem = ({
    accessory,
    className = ""
}: AccessoryItemProps) => {
    const { t } = useTranslation();

    // Find variant that corresponds to this accessory
    const variants = useSelector(selectAllVariants);
    const accessoryVariant = variants.find(v => v?.sizeId === accessory.id);
    const accesorySku = accessoryVariant?.sku || "";

    const dispatch = useDispatch();

    const { createUpdateAction } = createUiBindings();

    const setCanHaveBorderDraggedInto = createUpdateAction(
        "canHaveBorderDraggedInto"
    );

    const [isLocalDragging, setIsLocalDragging] = React.useState(false);

    const isMobile = useMobile();

    const accessoryIdMobile = useSelector(selectAccessoryId);
    const isActiveAccessory = accessoryIdMobile === accessory.id && isMobile;
    const isActiveClickToApply = accessoryIdMobile === accessory.id && !isMobile;

    const setDragging = (dragging: boolean) => {

        setIsLocalDragging(dragging);
        dispatch(setAccessoryIsDragging(dragging));

        if (dragging) {
            dispatch(setDraggedAccessory(accessory.id));
        } else {
            dispatch(setDraggedAccessory(""));
        }
    };

    const setPanelContentTab = (accessoryId: string | undefined) => {
        dispatch(setAccessoryId(accessoryId));
        dispatch(setFrameId(""));
    };

    const copy = accessory.label;

    const handleDragStart = (event: React.DragEvent<HTMLDivElement>) => {
        event.dataTransfer.setData("text/plain", "accessory");
        event.dataTransfer.setData("accessoryId", accessory.id);
        event.dataTransfer.setData("accessoryData", JSON.stringify(accessory));

        if (!accessory.minimumQuantity || accessory.minimumQuantity === 1) {
            event.dataTransfer.setData("singlePhotoAccessory", "true");
        }

        // to center the image while dragging in a scaled window
        event.dataTransfer.setDragImage(
            event.currentTarget,
            event.currentTarget.clientWidth / 2,
            event.currentTarget.clientHeight / 2
        );

        dispatch(setFrameId(""));
        dispatch(setAccessoryId(""));

        setDragging(true);
        dispatch(setCanHaveBorderDraggedInto(true));
    };

    const handleDragEnd = () => {
        setDragging(false);
        dispatch(setCanHaveBorderDraggedInto(false));
    };

    const handleClick = () => {

        if (isMobile) {
            dispatch(setFotoFunTab("accessories"));
            setPanelContentTab(isActiveAccessory ? "" : accessory.id);
        } else {
            dispatch(setAccessoryId(isActiveClickToApply ? "" : accessory.id));
            dispatch(setFrameId(""));
        }
    };

    const accessoryclassNames = classNames(
        styles.accessoryItem, className,
        {
            [styles.activeSquare]: isActiveAccessory || isActiveClickToApply
        }
    );

    return (
        <div
            className={accessoryclassNames}
            draggable={!isMobile}
            data-id={accessory.id}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
            onClick={handleClick}
        >
            <img
                src={urls.publicAssetUrl(accessory.iconAssetId)}
                alt={t(accessory.name)}
                className={styles.image}
                draggable={false}
            />
            <p className={styles.title}>{t(`${accessory.name}`) || accessory.label}</p>
            <p className={styles.copy}>{t(copy, { minimumQuantity: accessory.minimumQuantity || 1 })}</p>
            <div className={styles.priceLabel}>
                <RequestPriceLabel priceRequest={{
                    sku: accesorySku ?? ""
                }}/>
            </div>
            <DragCardHover isDragging={isLocalDragging} accessoryId={accessory.id} isClickActive={isActiveAccessory || isActiveClickToApply} />
        </div>
    );
};


const RequestPriceLabel = ({ priceRequest }: { priceRequest: {sku: string} }) => {

    const { t, i18n } = useTranslation();
    const { destinationRef, plainText, object } = useExternalFromPriceRender(priceRequest);

    return (
        <span ref={destinationRef}>
            {plainText && <>{plainText} {t('photo_prints.price_apiece')}</>}
            {object && <>{object.price
                .toLocaleString(
                    i18n.language,
                    { currency: object.currency, style: 'currency' }
                )} {t('photo_prints.price_apiece')}</>}
        </span>
    );
};